import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import {
  ZoneAnalysisConfig,
  ZoneParams,
  ZoneRectangle,
  zoneRectangleSchema,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { v4 } from 'uuid'
import { z } from 'zod'

import { replaceImageZoneProposals } from './zoneMutations'

/**
 * Parsing + conversion for AI zone suggestions served through the standard
 * assess endpoint (`POST /assess/section` with the ZoneAnalysis section's
 * `executionStrategy: "zone-suggestion"`).
 *
 * The backend emits proposals as the `zones` field's value with NORMALIZED
 * [0,1] bounds and never reports pixel sizes; conversion into the pixel
 * `drawing_bounds` the renderer stores happens HERE, using the
 * browser-measured natural image size (single source of truth — a
 * server-side decoder can disagree with the browser on EXIF-rotated or
 * re-encoded images).
 */

/** Section id of the ZoneAnalysis step's agent section in libra-workflow. */
export const ZONE_ANALYSIS_SECTION_ID = 'zone_analysis_section'

export const ZONE_COLORS = ['#FF8C00', '#3B82F6', '#22C55E', '#A855F7']

/**
 * Workflow-author-scoped extra permissions for the AI zone-suggest action,
 * read from the ZoneAnalysis section's config — so gating stays workflow-driven
 * exactly like every other agent section (see `AiAutofillBanner`). Base
 * Gecko-internal gating is applied on top by the caller; this returns only the
 * section's additions (empty when unconfigured).
 */
export function getZoneAgentPermissions(config: ZoneAnalysisConfig): string[] {
  return (
    config.sections?.find((s) => s.id === ZONE_ANALYSIS_SECTION_ID)
      ?.agentPermissions ?? []
  )
}

const normalizedCoord = z.number().finite().min(0).max(1)
const normalizedRectangleSchema = zoneRectangleSchema.extend({
  x_start: normalizedCoord,
  y_start: normalizedCoord,
  x_end: normalizedCoord,
  y_end: normalizedCoord,
})

const normalizedBoundsSchema = z.union([
  normalizedRectangleSchema,
  z.array(normalizedRectangleSchema).min(1),
])

/** One AI proposal exactly as the assess response carries it. */
export const zoneProposalSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  // The contract is NORMALIZED [0,1] — pixel-looking bounds are rejected so a
  // backend regression can't silently produce off-canvas boxes.
  bounds: normalizedBoundsSchema.transform((bounds) =>
    Array.isArray(bounds) ? bounds : [bounds]
  ),
  ai_meta: z
    .object({
      model: z.string().nullish(),
      generated_at: z.string().optional(),
    })
    .optional(),
})

export type ZoneProposal = z.infer<typeof zoneProposalSchema>

/**
 * Extract the zone proposals from an assess `field_values` payload.
 * Per-item tolerant: a malformed proposal is dropped, never thrown.
 */
export function parseZoneProposals(fieldValues: FieldValues): ZoneProposal[] {
  const raw = fieldValues.zones?.value
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const parsed = zoneProposalSchema.safeParse(item)
    return parsed.success ? [parsed.data] : []
  })
}

/**
 * Convert proposals into draft `ZoneParams` for one reference image.
 *
 * Re-suggesting REPLACES that image's still-unreviewed proposals instead of
 * piling duplicates on top; confirmed and hand-drawn zones are kept. Each
 * draft is flagged `ai_suggested` (pending SME review) with its original box
 * frozen in `ai_initial_bounds`.
 */
export function buildDraftZones({
  proposals,
  naturalSize,
  imageId,
  existingZones,
}: {
  proposals: ZoneProposal[]
  naturalSize: { width: number; height: number }
  imageId: string
  existingZones: ZoneParams[]
}): { keptZones: ZoneParams[]; newZones: ZoneParams[] } {
  const { width: w, height: h } = naturalSize
  const keptZones = replaceImageZoneProposals(existingZones, imageId, [])
  const existingForImage = keptZones.filter(
    (z) => z.zone_reference_image_id === imageId
  ).length
  const generatedAt = new Date().toISOString()

  const newZones: ZoneParams[] = proposals.map((s, i) => {
    const bounds: ZoneRectangle[] = s.bounds.map((box) => ({
      ...box,
      x_start: box.x_start * w,
      y_start: box.y_start * h,
      x_end: box.x_end * w,
      y_end: box.y_end * h,
    }))
    return {
      id: v4().substring(0, 6),
      title: s.title || `Zone ${existingForImage + i + 1}`,
      description: s.description,
      zone_reference_image_id: imageId,
      drawing_bounds: bounds,
      ai_initial_bounds: bounds.map((box) => ({ ...box })),
      ai_suggested: true,
      ai_meta: {
        model: s.ai_meta?.model ?? undefined,
        generated_at: s.ai_meta?.generated_at ?? generatedAt,
      },
      color: ZONE_COLORS[(existingForImage + i) % ZONE_COLORS.length],
      geometry: 'RECTANGLE',
      damage_mechanisms: [],
      zone_recommendations: [],
      assumptions: [],
    }
  })
  return { keptZones, newZones }
}
